import React, { useEffect, useState } from 'react';
import { jobsAPI } from '../services/websiteAPI';
import ConfirmModal from '../components/ConfirmModal';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    type: '',
    description: '',
    applyEmail: ''
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getAll();
      setJobs(response.data);
    } catch (err) {
      console.error('Failed to load jobs', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email format if emails are provided
    if (formData.applyEmail) {
      const emails = formData.applyEmail.split(',').map(e => e.trim()).filter(e => e);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      
      if (invalidEmails.length > 0) {
        alert(`Invalid email format: ${invalidEmails.join(', ')}`);
        return;
      }
    }
    
    try {
      if (editingId) {
        await jobsAPI.update(editingId, formData);
      } else {
        await jobsAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', location: '', type: '', description: '', applyEmail: '' });
      await loadJobs();
    } catch (err) {
      alert(`Failed to ${editingId ? 'update' : 'create'} job: ` + err.response?.data?.error);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await jobsAPI.getById(id);
      const job = response.data;
      setFormData({
        title: job.Title,
        location: job.Location || '',
        type: job.Type || '',
        description: job.Description || '',
        applyEmail: job.ApplyEmail || ''
      });
      setEditingId(id);
      setShowForm(true);
    } catch (err) {
      alert('Failed to load job for editing');
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', location: '', type: '', description: '', applyEmail: '' });
  };

  const handleDelete = async () => {
    try {
      await jobsAPI.delete(confirmDelete.id);
      await loadJobs();
      setConfirmDelete({ show: false, id: null });
    } catch (err) {
      alert('Failed to delete job');
      setConfirmDelete({ show: false, id: null });
    }
  };

  if (loading) return <div className="loading">Loading jobs...</div>;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Career Opportunities</h1>
        <button onClick={() => editingId ? handleCancelEdit() : setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Cancel' : 'Add Job Posting'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Edit Job Posting' : 'New Job Posting'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Job Title *</label>
              <input
                type="text"
                placeholder="e.g., Technical Recruiter"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Location</label>
              <input
                type="text"
                placeholder="e.g., Rockleigh, NJ (Hybrid)"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Job Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Select Type</option>
                <option value="Full-Time">Full-Time</option>
                <option value="Part-Time">Part-Time</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
                <option value="Internship">Internship</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
              <textarea
                placeholder="Job description, responsibilities, and requirements"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="6"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Apply Email(s)</label>
              <input
                type="text"
                placeholder="e.g., solutions@rsc.com, hr@rsc.com"
                value={formData.applyEmail}
                onChange={(e) => setFormData({ ...formData, applyEmail: e.target.value })}
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Enter multiple emails separated by commas</small>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="jobs-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
        {jobs.map((job) => (
          <div key={job.JobId} className="card" style={{ height: '100%' }}>
            <h3 style={{ marginTop: 0, color: '#0078d4' }}>{job.Title}</h3>
            <div style={{ marginBottom: '10px' }}>
              <strong>Location:</strong> {job.Location || 'Not specified'}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Type:</strong> {job.Type || 'Not specified'}
            </div>
            {job.Description && (
              <div style={{ marginBottom: '10px', color: '#666', fontSize: '14px' }}>
                {job.Description.length > 150 
                  ? job.Description.substring(0, 150) + '...' 
                  : job.Description}
              </div>
            )}
            {job.ApplyEmail && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Apply:</strong>{' '}
                {job.ApplyEmail.split(',').map((email, index, array) => (
                  <span key={index}>
                    <a href={`mailto:${email.trim()}`}>{email.trim()}</a>
                    {index < array.length - 1 && ', '}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
              <button onClick={() => handleEdit(job.JobId)} className="btn btn-sm btn-secondary">
                Edit
              </button>
              <button onClick={() => setConfirmDelete({ show: true, id: job.JobId })} className="btn btn-sm btn-danger">
                Delete
              </button>
            </div>
          </div>
        ))}
        {jobs.length === 0 && (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            No job postings found. Click "Add Job Posting" to create one.
          </div>
        )}
      </div>

      {confirmDelete.show && (
        <ConfirmModal
          show={confirmDelete.show}
          title="Delete Job Posting"
          message="Are you sure you want to delete this job posting? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete({ show: false, id: null })}
        />
      )}
    </div>
  );
};

export default Careers;
